import React, { useCallback, useEffect, useState } from "react";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../lib/firebase";
import { useAuth } from "../context/AuthProvider";
import { Card } from "../components/common/Card";
import { Spinner } from "../components/common/Spinner";
import { toast } from "react-hot-toast";
import {
  Calendar,
  Utensils,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  Ban,
  SkipForward,
  Undo2,
  Plus,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import {
  pauseSubscription,
  resumeSubscription,
  cancelSubscription,
  skipTiffinDay,
  unskipTiffinDay,
  renewSubscription,
} from "../services/subscription.service";
import { callableMessage } from "../services/callables";
import type { Subscription, SubscriptionStatus } from "../types/index";

interface DailyTiffin {
  id: string;
  subscriptionId: string;
  date: string;
  status: string;
  meals: string[];
  items: any[];
  createdAt: any;
  skipReason?: string;
  skippedItems?: { name?: string; id?: string; reason?: string; mealType?: string }[];
}

type DayFilter = "all" | "upcoming" | "skipped" | "completed";

const formatSubDate = (value: any): string => {
  if (!value) return "—";
  try {
    if (typeof value?.toDate === "function") {
      return format(value.toDate(), "dd MMM yyyy");
    }
    if (value instanceof Date) return format(value, "dd MMM yyyy");
    if (typeof value === "string") {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return format(d, "dd MMM yyyy");
      return value;
    }
    if (typeof value?.seconds === "number") {
      return format(new Date(value.seconds * 1000), "dd MMM yyyy");
    }
  } catch {
    /* ignore */
  }
  return "—";
};

const toJsDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const dayKey = (d: Date) => format(d, "yyyy-MM-dd");
const keyToDate = (k: string) => new Date(k + "T00:00:00");
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/** Last day with meals and days left (counting today) for fixed-duration plans; null for open-ended ones. */
const planWindow = (sub: Subscription): { lastDay: Date; daysLeft: number } | null => {
  const start = toJsDate(sub.startDate);
  if (!start) return null;
  const startDay = keyToDate(dayKey(start));
  let duration = Number((sub as any).durationDays);
  if (!Number.isInteger(duration) || duration < 1) {
    const end = toJsDate((sub as any).endDate);
    if (!end) return null;
    duration = Math.max(1, Math.round((keyToDate(dayKey(end)).getTime() - startDay.getTime()) / 86400000));
  }
  const endDay = addDays(startDay, duration); // first day after the plan
  const today = keyToDate(dayKey(new Date()));
  const daysLeft = Math.max(0, Math.round((endDay.getTime() - today.getTime()) / 86400000));
  return { lastDay: addDays(endDay, -1), daysLeft };
};

const RENEW_PROMPT_DAYS = 3;

const planLabel = (sub: Subscription): string => {
  if (sub.planType) {
    return String(sub.planType).replace(/_/g, " ");
  }
  if (sub.durationDays) return `${sub.durationDays}-day plan`;
  return "Meal plan";
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case "active":
    case "scheduled":
      return "bg-blue-100 text-blue-800";
    case "paused":
      return "bg-amber-100 text-amber-800";
    case "skipped":
      return "bg-orange-100 text-orange-800";
    case "preparing":
      return "bg-yellow-100 text-yellow-800";
    case "ready":
    case "ordered":
    case "delivered":
    case "completed":
      return "bg-green-100 text-green-800";
    case "delivering":
      return "bg-purple-100 text-purple-800";
    case "cancelled":
    case "expired":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const MyTiffin: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [tiffins, setTiffins] = useState<DailyTiffin[]>([]);
  const [filter, setFilter] = useState<DayFilter>("upcoming");

  const loadSubscriptions = useCallback(async () => {
    if (!user) return;
    const subsRef = collection(db, "subscriptions");
    const q = query(subsRef, where("userId", "==", user.uid));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as Subscription[];

    // Prefer active/paused first, then by start date desc
    data.sort((a, b) => {
      const rank = (s: SubscriptionStatus | string) => {
        if (s === "active") return 0;
        if (s === "paused") return 1;
        if (s === "scheduled") return 2;
        return 3;
      };
      const r = rank(a.status) - rank(b.status);
      if (r !== 0) return r;
      const aTime =
        a.startDate?.toDate?.()?.getTime?.() ??
        (a.startDate?.seconds ? a.startDate.seconds * 1000 : 0);
      const bTime =
        b.startDate?.toDate?.()?.getTime?.() ??
        (b.startDate?.seconds ? b.startDate.seconds * 1000 : 0);
      return bTime - aTime;
    });

    setSubscriptions(data);
  }, [user]);

  const loadTiffins = useCallback(async () => {
    if (!user) return;
    const tiffinsRef = collection(db, "daily_tiffins");
    const q = query(
      tiffinsRef,
      where("userId", "==", user.uid),
      orderBy("date", "desc")
    );

    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as DailyTiffin[];

    const today = format(new Date(), "yyyy-MM-dd");
    let filtered = data;

    if (filter === "upcoming") {
      filtered = data.filter(
        (t) =>
          t.date >= today &&
          (t.status === "scheduled" || t.status === "skipped")
      );
      // Upcoming: show soonest first
      filtered = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
    } else if (filter === "skipped") {
      filtered = data.filter((t) => t.status === "skipped");
    } else if (filter === "completed") {
      // Tiffins end at "ordered" once every meal has gone to the kitchen (delivery is tracked on the
      // kitchen order), so past "ordered" days count as completed too.
      filtered = data.filter(
        (t) =>
          t.status === "delivered" ||
          t.status === "completed" ||
          (t.status === "ordered" && t.date < today)
      );
    }

    setTiffins(filtered);
  }, [user, filter]);

  const reloadAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      await Promise.all([loadSubscriptions(), loadTiffins()]);
    } catch (error) {
      console.error("Error loading subscription data:", error);
      toast.error("Couldn’t load your subscription. Pull to refresh.");
    } finally {
      setLoading(false);
    }
  }, [user, loadSubscriptions, loadTiffins]);

  useEffect(() => {
    if (user) {
      reloadAll();
    }
  }, [user, filter, reloadAll]);

  const runSubAction = async (
    id: string,
    action: "pause" | "resume" | "cancel"
  ) => {
    const labels = {
      pause: "Pause this subscription? Upcoming meals stay listed but delivery pauses.",
      resume: "Resume this subscription?",
      cancel:
        "Cancel this subscription? This cannot be undone from here. Skipped/cancelled days won’t auto-refund.",
    };
    if (!window.confirm(labels[action])) return;

    setActionId(id);
    try {
      if (action === "pause") await pauseSubscription(id);
      if (action === "resume") await resumeSubscription(id);
      if (action === "cancel") await cancelSubscription(id);
      toast.success(
        action === "pause"
          ? "Subscription paused"
          : action === "resume"
            ? "Subscription resumed"
            : "Subscription cancelled"
      );
      await loadSubscriptions();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || `Failed to ${action}`);
    } finally {
      setActionId(null);
    }
  };

  const handleRenew = async (sub: Subscription) => {
    const id = sub.id!;
    setActionId(id);
    try {
      const q = await renewSubscription(id, true);
      const fmt = (k: string) => format(keyToDate(k), "EEE, dd MMM");
      const lines = [
        `Renew for ${(sub as any).durationDays || ""} days: ${fmt(q.startDate)} → ${fmt(q.lastDay)}.`,
        `Same meals and dishes as this plan, at today's menu prices: about ₹${Number(q.totalAmount).toLocaleString("en-IN")} for ${q.days} day(s).`,
        "Each meal is charged from your wallet when the kitchen starts it.",
      ];
      if (q.dropped?.length) {
        const names = Array.from(new Set(q.dropped.filter((d) => !d.backup).map((d) => d.name))).slice(0, 4).join(", ");
        if (names) lines.push(`Left out (no longer on the menu): ${names}.`);
      }
      if (!window.confirm(lines.join("\n\n"))) return;
      const res = await renewSubscription(id, false);
      toast.success(`Renewed! New plan starts ${fmt(res.startDate)}.`);
      await reloadAll();
    } catch (err: any) {
      console.error(err);
      toast.error(callableMessage(err, "Couldn't renew this plan."));
    } finally {
      setActionId(null);
    }
  };

  const handleSkip = async (tiffin: DailyTiffin) => {
    if (
      !window.confirm(
        `Skip tiffin for ${format(new Date(tiffin.date), "EEE, dd MMM")}? You won’t receive meals that day.`
      )
    ) {
      return;
    }
    setActionId(tiffin.id);
    try {
      await skipTiffinDay(tiffin.id);
      toast.success("Day skipped");
      await loadTiffins();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to skip day");
    } finally {
      setActionId(null);
    }
  };

  const handleUnskip = async (tiffin: DailyTiffin) => {
    if (
      !window.confirm(
        `Restore tiffin for ${format(new Date(tiffin.date), "EEE, dd MMM")}?`
      )
    ) {
      return;
    }
    setActionId(tiffin.id);
    try {
      await unskipTiffinDay(tiffin.id);
      toast.success("Day restored");
      await loadTiffins();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to unskip day");
    } finally {
      setActionId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "ordered":
      case "delivered":
      case "completed":
        return <CheckCircle className="text-green-600" size={20} />;
      case "cancelled":
        return <XCircle className="text-red-600" size={20} />;
      case "skipped":
        return <SkipForward className="text-orange-600" size={20} />;
      case "paused":
        return <Pause className="text-amber-600" size={20} />;
      default:
        return <Clock className="text-blue-600" size={20} />;
    }
  };

  if (loading && subscriptions.length === 0 && tiffins.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size={48} />
        <p className="ml-4 text-gray-500">Loading your subscription…</p>
      </div>
    );
  }

  const filters: { key: DayFilter; label: string }[] = [
    { key: "upcoming", label: "Upcoming" },
    { key: "skipped", label: "Skipped" },
    { key: "completed", label: "Completed" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
              <Utensils className="text-orange-600 shrink-0" size={28} />
              My Subscription
            </h1>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">
              Manage plans, pause, cancel, or skip individual days
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate("/today-tiffin")}
              className="flex-1 sm:flex-none px-4 py-2.5 bg-white border border-orange-200 text-orange-700 rounded-xl font-bold hover:bg-orange-50 transition-colors shadow-sm text-sm min-h-[44px]"
            >
              Today’s tiffin
            </button>
            <button
              type="button"
              onClick={() => navigate("/subscription")}
              className="flex-1 sm:flex-none px-4 py-2.5 bg-orange-600 text-white rounded-xl font-bold hover:bg-orange-700 transition-colors shadow-md flex items-center justify-center gap-2 text-sm min-h-[44px]"
            >
              <Plus size={18} />
              New plan
            </button>
          </div>
        </div>

        {/* MY SUBSCRIPTIONS */}
        <section className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-3">
            My subscriptions
          </h2>
          {subscriptions.length === 0 ? (
            <Card className="p-6 text-center">
              <AlertCircle className="mx-auto text-gray-300 mb-3" size={40} />
              <p className="font-semibold text-gray-800 mb-1">
                No subscriptions yet
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Start a meal plan to see it here.
              </p>
              <button
                type="button"
                onClick={() => navigate("/subscription")}
                className="px-5 py-2.5 bg-orange-600 text-white rounded-xl font-bold text-sm"
              >
                Browse plans
              </button>
            </Card>
          ) : (
            <div className="space-y-3">
              {subscriptions.map((sub) => {
                const id = sub.id!;
                const busy = actionId === id;
                const canPause = sub.status === "active";
                const canResume = sub.status === "paused";
                const canCancel =
                  sub.status === "active" ||
                  sub.status === "paused" ||
                  sub.status === "scheduled";
                const win = planWindow(sub);
                const renewedBy = (sub as any).renewedBySubscriptionId as string | undefined;
                const renewal = renewedBy ? subscriptions.find((x) => x.id === renewedBy) : undefined;
                const alreadyRenewed = !!renewedBy && (!renewal || renewal.status !== "cancelled");
                const nearEnd = sub.status === "active" && !!win && win.daysLeft <= RENEW_PROMPT_DAYS;
                const canRenew = !!win && !alreadyRenewed && (nearEnd || sub.status === "expired");

                return (
                  <Card
                    key={id}
                    className="p-4 sm:p-5 border border-orange-50"
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-bold text-gray-900 capitalize">
                            {planLabel(sub)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatSubDate(sub.startDate)} →{" "}
                            {formatSubDate(sub.endDate)}
                            {sub.durationDays
                              ? ` · ${sub.durationDays} days`
                              : ""}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${statusBadgeClass(
                            sub.status
                          )}`}
                        >
                          {sub.status}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        {(sub.meals || []).map((meal) => (
                          <span
                            key={meal}
                            className="px-2.5 py-1 bg-blue-50 text-blue-800 rounded-full text-xs font-medium capitalize"
                          >
                            {String(meal).replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>

                      {nearEnd && canRenew && win && (
                        <div className="flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-800">
                          <RefreshCw size={14} className="shrink-0" />
                          <span>
                            {win.daysLeft === 0
                              ? "Your plan has ended."
                              : win.daysLeft === 1
                                ? "Today is the last day of your plan."
                                : `${win.daysLeft} days left on your plan (last day ${format(win.lastDay, "dd MMM")}).`}{" "}
                            Renew to keep the same meals coming.
                          </span>
                        </div>
                      )}
                      {alreadyRenewed && renewal && (
                        <p className="text-xs text-gray-500">
                          Renewed · next plan starts {formatSubDate(renewal.startDate)}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-gray-100">
                        <p className="text-sm font-bold text-gray-900">
                          ₹{Number(sub.totalAmount || 0).toLocaleString("en-IN")}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {canRenew && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => handleRenew(sub)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-orange-300 bg-orange-600 text-white text-xs font-bold min-h-[40px] disabled:opacity-50"
                            >
                              <RefreshCw size={14} />
                              Renew
                            </button>
                          )}
                          {canPause && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => runSubAction(id, "pause")}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs font-bold min-h-[40px] disabled:opacity-50"
                            >
                              <Pause size={14} />
                              Pause
                            </button>
                          )}
                          {canResume && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => runSubAction(id, "resume")}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-200 bg-green-50 text-green-800 text-xs font-bold min-h-[40px] disabled:opacity-50"
                            >
                              <Play size={14} />
                              Resume
                            </button>
                          )}
                          {canCancel && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => runSubAction(id, "cancel")}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-bold min-h-[40px] disabled:opacity-50"
                            >
                              <Ban size={14} />
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* CALENDAR / UPCOMING DAYS */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Calendar className="text-orange-600" size={20} />
            Upcoming days
          </h2>

          <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`shrink-0 px-4 py-2 rounded-xl font-medium text-sm transition-all min-h-[40px] ${
                  filter === f.key
                    ? "bg-orange-600 text-white shadow-md"
                    : "bg-white text-gray-700 hover:bg-gray-50 border"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {tiffins.length === 0 ? (
            <Card className="p-10 text-center">
              <Calendar className="mx-auto text-gray-300 mb-4" size={56} />
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                No tiffins found
              </h3>
              <p className="text-gray-500 text-sm">
                {filter === "upcoming"
                  ? "You don’t have any upcoming meals scheduled."
                  : filter === "skipped"
                    ? "No skipped days."
                    : "No tiffins match this filter."}
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {tiffins.map((tiffin) => {
                const busy = actionId === tiffin.id;
                const canSkip = tiffin.status === "scheduled";
                const canUnskip = tiffin.status === "skipped";

                return (
                  <Card
                    key={tiffin.id}
                    className="p-4 sm:p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar
                            className="text-orange-600 shrink-0"
                            size={20}
                          />
                          <h3 className="text-base sm:text-lg font-bold text-gray-800">
                            {format(
                              new Date(tiffin.date + "T00:00:00"),
                              "EEE, dd MMM yyyy"
                            )}
                          </h3>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {(tiffin.meals || []).map((meal) => (
                            <span
                              key={meal}
                              className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium capitalize"
                            >
                              {meal.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>

                        <div className="space-y-1.5">
                          {(tiffin.items || []).map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 text-sm"
                            >
                              <Utensils
                                size={14}
                                className="text-gray-400 shrink-0"
                              />
                              <span className="text-gray-700 font-medium truncate">
                                {item.name}
                              </span>
                              {item.backupItem && (
                                <span className="text-xs text-gray-500 truncate">
                                  (Backup: {item.backupItem.name})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>

                        {(tiffin.skippedItems || []).length > 0 && (
                          <div className="mt-2 space-y-0.5">
                            {(tiffin.skippedItems || []).map((s, i) => (
                              <p key={i} className="text-xs text-orange-700">
                                Not cooked or charged: {s.name || s.id}
                                {s.reason ? ` (${s.reason})` : ""}
                              </p>
                            ))}
                          </div>
                        )}

                        {tiffin.status === "skipped" && tiffin.skipReason && (
                          <p className="text-xs text-orange-700 mt-2">
                            {tiffin.skipReason}
                          </p>
                        )}
                      </div>

                      <div className="flex sm:flex-col items-center sm:items-end gap-2 shrink-0">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(tiffin.status)}
                          <span
                            className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize ${statusBadgeClass(
                              tiffin.status
                            )}`}
                          >
                            {tiffin.status}
                          </span>
                        </div>

                        {canSkip && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleSkip(tiffin)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-orange-200 bg-orange-50 text-orange-800 text-xs font-bold min-h-[40px] disabled:opacity-50 w-full sm:w-auto justify-center"
                          >
                            <SkipForward size={14} />
                            Skip day
                          </button>
                        )}
                        {canUnskip && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleUnskip(tiffin)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 text-xs font-bold min-h-[40px] disabled:opacity-50 w-full sm:w-auto justify-center"
                          >
                            <Undo2 size={14} />
                            Unskip
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MyTiffin;
